import {
  CardContent,
  Typography,
  Divider,
  TextField,
  Button,
  Stack,
  Grid2,
} from "@mui/material";
import { useTranslation } from "../../../hooks/useTranslation";
import { DeliveryOrder, ShippingAddress } from "../../orders/utils/types";
import { useForm, Controller } from "react-hook-form";
import { useState } from "react";
import { useCustomMutation } from "@refinedev/core";
import { useMarketplaceStore } from "../../../store/useMarketplaceStore";
import { fieldGroups } from "./addressUtils";
import { useParams } from "react-router-dom";
import { Row } from "./ValueRow";
import { SyncrosaleLoader } from "../../../components/layout/components/SyncrosaleLoader";
import { useQueryClient } from "@tanstack/react-query";

type Props = {
  deliveryOrder: DeliveryOrder;
};

export const OrderAddress = ({ deliveryOrder }: Props) => {
  const { orderId } = useParams<{ orderId: string }>();
  const { translate, translateWithoutPrefix } = useTranslation(
    "pages.orders.detail",
  );
  const selectedCountry = useMarketplaceStore((state) => state.selectedCountry);
  const [isEditing, setIsEditing] = useState(false);
  const { control, handleSubmit, reset } = useForm({
    defaultValues: deliveryOrder.shippingAddress,
  });
  const queryClient = useQueryClient();
  const { mutate, isLoading: isSaveLoading } = useCustomMutation();

  const onSubmit = async (data: ShippingAddress) => {
    const dataToSend = { ...data };

    Object.keys(dataToSend).forEach((key) => {
      if (dataToSend[key as keyof ShippingAddress] === "") {
        delete dataToSend[key as keyof ShippingAddress];
      }
    });

    mutate(
      {
        url: `store/${selectedCountry?.id}/order/${orderId}/address`,
        method: "put",
        values: dataToSend,
        successNotification: () => ({
          message: translate("updatedSuccessfully"),
          type: "success",
        }),
        meta: {
          queries: [
            {
              field: "force-update",
              value: "true",
            },
          ],
        },
      },
      {
        onSuccess: () => {
          (async () => {
            handleCancel();

            const queryKey = [
              "order-detail",
              selectedCountry?.id,
              String(orderId),
            ];

            await queryClient.invalidateQueries({ queryKey });

            await queryClient.refetchQueries({
              queryKey,
            });
          })();
        },
      },
    );
  };

  const handleCancel = () => {
    reset(deliveryOrder.shippingAddress);
    setIsEditing(false);
  };

  const handleEdit = () => {
    reset(deliveryOrder.shippingAddress);
    setIsEditing(true);
  };

  const renderField = (field: keyof ShippingAddress) =>
    isEditing ? (
      <Controller
        key={field}
        name={field}
        control={control}
        render={({ field: controllerField }) => (
          <TextField
            {...controllerField}
            fullWidth
            label={translate("addressFields." + field)}
            margin="dense"
            size="small"
          />
        )}
      />
    ) : (
      <Row
        label={translate("addressFields." + field)}
        value={deliveryOrder.shippingAddress?.[field]}
      />
    );

  if (isSaveLoading) return <SyncrosaleLoader />;

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <CardContent>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            spacing={2}
          >
            <Typography variant="h6" gutterBottom>
              {translate("shippingAddress")}
            </Typography>

            <Typography>
              {translate("addressSource")}:{" "}
              {deliveryOrder.manualAddressOverride
                ? translate("manual")
                : translate("sellerCentral")}
            </Typography>
          </Stack>

          {!isEditing ? (
            <Button variant="outlined" onClick={handleEdit} size="small">
              {translateWithoutPrefix("buttons.edit")}
            </Button>
          ) : (
            <Stack direction="row" spacing={1}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                size="small"
              >
                {translateWithoutPrefix("buttons.save")}
              </Button>
              <Button variant="outlined" onClick={handleCancel} size="small">
                {translateWithoutPrefix("buttons.cancel")}
              </Button>
            </Stack>
          )}
        </Stack>

        <Divider sx={{ my: 1 }} />

        <Grid2 container spacing={2}>
          {fieldGroups.map((group) => (
            <Grid2 key={group.side} size={{ xs: 12, md: 6 }}>
              {group.fields.map(renderField)}
            </Grid2>
          ))}
        </Grid2>
      </CardContent>
    </form>
  );
};
